import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';

import { toRequestSortModel } from '@/components/AgGrid/helpers/sortModel';
import { bankFeedApi } from '@/domain/bankFeed/api';
import type { BankFeedAccountType, BankFeedTransactionRow } from '@/domain/bankFeed/schemas';
import { handleErrorResponseWithToast } from '@/lib/toasts';
import type { ToolbarFilters } from '@/pages/company/BankFeedRoute/components/FilterToolbar/types';
import { buildTransactionsDatePayload } from '@/pages/company/BankFeedRoute/components/FilterToolbar/dateRange';

const gridLoadFailedToast = {
  id: 'app.error.loadFailed',
  defaultMessage: 'Could not load data',
};

type UseServerDatasourceArgs = {
  accountType: BankFeedAccountType;
  toolbarFilters: ToolbarFilters;
};

type CursorToken = string | null; // backend treats this as opaque; FE passes through

type CursorPair = {
  searchAfter: CursorToken;
  searchBefore: CursorToken;
};

const EMPTY_CURSORS: CursorPair = { searchAfter: null, searchBefore: null };

export type UseServerDatasourceResult = {
  datasource: IServerSideDatasource<BankFeedTransactionRow>;
  resetCursors: () => void;
};

const useServerDatasource = ({
  accountType,
  toolbarFilters,
}: UseServerDatasourceArgs): UseServerDatasourceResult => {
  const filtersRef = useRef<ToolbarFilters>(toolbarFilters);
  // Cursor pagination: each response carries `search_after` (forward token) + `search_before`
  // (backward token). FE keeps only the most-recent response's cursors and picks which one to
  // send based on navigation direction (detected by comparing the new request's `startRow`
  // against the previous one). Reset to null on filter/sort/search change — cursors don't
  // survive query changes. Page-0 always sends both as null (first page is anchorless).
  const cursorsRef = useRef<CursorPair>(EMPTY_CURSORS);
  const prevStartRowRef = useRef<number | null>(null);

  useEffect(() => {
    filtersRef.current = toolbarFilters;
  }, [toolbarFilters]);

  const resetCursors = useCallback(() => {
    cursorsRef.current = EMPTY_CURSORS;
    prevStartRowRef.current = null;
  }, []);

  const datasource = useMemo<IServerSideDatasource<BankFeedTransactionRow>>(
    () => ({
      getRows: async (params: IServerSideGetRowsParams<BankFeedTransactionRow>) => {
        const { success, request } = params;

        const startRow = request.startRow ?? 0;
        const endRow = request.endRow ?? startRow;
        const prevStartRow = prevStartRowRef.current;

        // Direction → which cursor to send.
        //   first | same  → null/null (anchorless first page; refresh re-fetches from start)
        //   forward       → search_after from the previous response, search_before null
        //   backward      → search_before from the previous response, search_after null
        // BE infers navigation direction from which token is non-null; sending both would
        // ambiguate the request.
        const isFirstFetch = prevStartRow === null || startRow === 0;
        const isForward = !isFirstFetch && startRow > prevStartRow;
        const isBackward = !isFirstFetch && startRow < prevStartRow;

        const searchAfter = isForward ? cursorsRef.current.searchAfter : null;
        const searchBefore = isBackward ? cursorsRef.current.searchBefore : null;

        const current = filtersRef.current;
        // Invariant: BankFeedRoute gates `isGridReady` on `toolbarFilters.currency != null`
        // before mounting this datasource (see BankFeedRoute/index.tsx). If this branch fires,
        // either the gate broke or filtersRef captured a stale pre-default snapshot — bail
        // gracefully so the grid shows "no rows" instead of sending a malformed request.
        if (current.currency == null) {
          success({ rowData: [], rowCount: 0 });
          return;
        }

        try {
          const response = await bankFeedApi.getTransactionsGridSearch({
            body: {
              sort_model: toRequestSortModel(request.sortModel),
              // AG Grid types `request.filterModel` as `FilterModel | AdvancedFilterModel`
              // (objects with implicit-any index signatures); the wire contract is "any JSON
              // object" which the schema models as Record<string, unknown>. Cast documents
              // the boundary between AG Grid's typed envelope and our wire schema.
              filter_model: (request.filterModel ?? {}) as Record<string, unknown>,
              account_type: accountType,
              account_ids: current.accountIds,
              currency: current.currency,
              text: current.search.length > 0 ? [current.search] : [],
              limit: endRow - startRow,
              search_after: searchAfter,
              search_before: searchBefore,
              ...buildTransactionsDatePayload(accountType, current),
            },
          });

          const {
            items,
            count,
            search_after: nextSearchAfter,
            search_before: nextSearchBefore,
          } = response;

          success({ rowData: items, rowCount: count });
          cursorsRef.current = { searchAfter: nextSearchAfter, searchBefore: nextSearchBefore };
          prevStartRowRef.current = startRow;
        } catch (error) {
          handleErrorResponseWithToast(error, { defaultMessage: gridLoadFailedToast });
          success({ rowData: [], rowCount: 0 });
        }
      },
    }),
    [accountType],
  );

  return { datasource, resetCursors };
};

export default useServerDatasource;
